import { useCallback } from 'react';

import { useTheme } from '@mui/material';
import { useSelector } from 'react-redux';

import type {
  ExtendedOptions,
  ExtendedSeriesOptionsType
} from '@/components/Chart/types/highcharts';
import type { RootState } from '@/store';

import useChartFunctions from './useChartFunctions';
import createRangeSelector from '../useRangeSelector';

const useBaseChartOptions = (height: number) => {
  const theme = useTheme();
  const { userConfig } = useSelector((state: RootState) => state.userConfig);
  const { createNavigator, createPlotOptions, createYAxis, createXAxis } = useChartFunctions();

  const createBaseChartOptions = useCallback(
    (
      chartSeries: ExtendedSeriesOptionsType[],
      textColor: string,
      backgroundColor: string
    ): ExtendedOptions => {
      return {
        accessibility: { enabled: false },
        time: { timezone: userConfig.timezone.value },
        title: {
          text: undefined
        },
        colors: [],
        chart: {
          reflow: true,
          animation: false,
          type: 'line',
          backgroundColor,
          height,
          style: {
            fontFamily: 'Cairo, sans-serif'
          },
          zooming: {
            type: 'x'
          }
        },
        exporting: {
          scale: 1,
          enabled: true,
          filename: 'GX-Chart',
          chartOptions: {
            legend: {
              enabled: true,
              itemStyle: {
                color: textColor,
                fontSize: '0.8em',
                textOverflow: 'ellipsis'
              }
            },
            plotOptions: {
              series: {
                dataLabels: {
                  enabled: false
                }
              }
            },
            navigator: {
              enabled: false
            },
            rangeSelector: { enabled: false },
            scrollbar: {
              enabled: false
            }
          }
        },
        xAxis: createXAxis(textColor),
        yAxis: createYAxis(textColor),

        plotOptions: createPlotOptions(),
        series: chartSeries,
        legend: { enabled: false },
        navigator: createNavigator(theme),
        rangeSelector: createRangeSelector(theme, chartSeries),
        credits: {
          enabled: false
        }
      };
    },
    [
      createNavigator,
      createPlotOptions,
      createXAxis,
      createYAxis,
      height,
      theme,
      userConfig.timezone.value
    ]
  );

  return { createBaseChartOptions };
};

export default useBaseChartOptions;
